import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './ContractorDashboard.css';
import allSafeLogo from '../../../assets/cloudsafe-logo.png';
import { 
  FaClipboardList,
  FaFileAlt,
  FaTasks,
  FaCheckCircle,
  FaClock,
  FaUserCircle,
  FaBell,
  FaSearch,
  FaUsers,
  FaBuilding,
  FaCertificate,
  FaCalendarAlt,
  FaChartLine,
  FaShieldAlt,
  FaEdit,
  FaEye,
  FaDownload,
  FaUpload,
  FaCalendar,
  FaFileSignature,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaPrint,
  FaShare,
  FaArchive,
  FaBars,
  FaTimes
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import {
  archiveContractorReport,
  generateContractorReport,
  getContractorReportDownloadUrl,
  getContractorReports,
  uploadContractorReportFiles
} from '../../../services/contractorService';
import { getApiEndpoint } from '../../../config/api';

const ContractorReports = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [reports, setReports] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, draft, completed, sent
  const [sortBy, setSortBy] = useState('date'); // date, type, status, property
  const [timeframe, setTimeframe] = useState('month'); // week, month, quarter, year

  useEffect(() => {
    // Verify user is contractor
    const userRole = getUserRole();
    if (userRole !== 'contractor') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }

    fetchReportsData();
  }, [navigate]);

  const fetchReportsData = async () => {
    try {
      // Get user profile
      const profileResponse = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setUserName(`${profileData.firstName} ${profileData.lastName}`);
      }

      const reportRows = await getContractorReports();
      setReports(reportRows.map((report) => {
        const data = typeof report.data === 'string' ? JSON.parse(report.data || '{}') : (report.data || {});
        const reportDate = report.generated_date ? new Date(report.generated_date) : new Date();
        return {
          id: report.id,
          property_id: report.property_id,
          title: report.title,
          property: report.property_name || `Property #${report.property_id}`,
          client: data.client || 'Client',
          reportType: report.type || 'inspection',
          category: data.category || 'General',
          date: reportDate.toISOString().split('T')[0],
          status: (report.status || 'submitted').replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          format: data.format || 'PDF',
          pages: data.pages || 1,
          findings: data.findings || 'No findings attached.',
          compliance: data.compliance || 'Unknown',
          nextInspection: data.nextInspection || null,
          photos: Array.isArray(data.uploadedFiles) ? data.uploadedFiles.length : (data.photos || 0),
          attachments: Array.isArray(data.uploadedFiles) ? data.uploadedFiles.length : (data.attachments || 0),
          reportNumber: data.reportNumber || `CR-${report.id}`,
          inspectionDate: data.inspectionDate || reportDate.toISOString().split('T')[0],
          priority: data.priority || 'Medium'
        };
      }));
      
    } catch (error) {
      console.error('Error fetching reports data:', error);
      toast.error('Failed to load reports data');
      setReports([]);
    }
  };

  const loadSimulatedReportsData = () => {
    setUserName('John Smith');
    
    // Simulated reports data
    setReports([
      {
        id: 1,
        title: 'Roof Safety Inspection Report - ABC Tower',
        property: 'ABC Corporate Tower',
        client: 'ABC Corporation',
        reportType: 'Safety Inspection',
        category: 'Roof Safety',
        date: '2024-04-12',
        status: 'Completed',
        format: 'PDF',
        pages: 15,
        findings: 'All anchor points passed inspection. Minor wear noted on static line #3.',
        compliance: 'Compliant',
        nextInspection: '2025-04-12',
        photos: 12,
        attachments: 3,
        reportNumber: 'RS-2024-001',
        inspectionDate: '2024-04-10',
        priority: 'High'
      },
      {
        id: 2,
        title: 'Fire Safety System Report - Shopping Center',
        property: 'Riverside Shopping Center',
        client: 'City Mall Management',
        reportType: 'Safety Inspection',
        category: 'Fire Safety',
        date: '2024-04-11',
        status: 'Pending Review',
        format: 'PDF',
        pages: 22,
        findings: '2 fire extinguishers require service. Smoke detector batteries need replacement on floor 2.',
        compliance: 'Non-Compliant',
        actions: 'Schedule fire extinguisher service. Replace smoke detector batteries.',
        deadline: '2024-04-20',
        photos: 18,
        attachments: 5,
        reportNumber: 'FS-2024-003',
        inspectionDate: '2024-04-09',
        priority: 'High'
      },
      {
        id: 3,
        title: 'Monthly Building Maintenance Report',
        property: 'Industrial Warehouse',
        client: 'Warehouse Corp',
        reportType: 'Maintenance Report',
        category: 'Building Maintenance',
        date: '2024-04-08',
        status: 'Sent',
        format: 'PDF',
        pages: 8,
        findings: 'All systems operational. Minor plumbing leak detected and repaired.',
        compliance: 'Compliant',
        photos: 6,
        attachments: 2,
        reportNumber: 'BM-2024-012',
        inspectionDate: '2024-04-05',
        priority: 'Medium'
      },
      {
        id: 4,
        title: 'Electrical Safety Assessment Report',
        property: 'Medical Center',
        client: 'HealthCare Group',
        reportType: 'Safety Assessment',
        category: 'Electrical Safety',
        date: '2024-04-07',
        status: 'Draft',
        format: 'PDF',
        pages: 18,
        findings: 'RCD testing completed successfully. PAT testing identified 3 items requiring replacement.',
        compliance: 'Partially Compliant',
        actions: 'Replace identified electrical equipment. Re-test within 30 days.',
        deadline: '2024-05-07',
        photos: 14,
        attachments: 4,
        reportNumber: 'ES-2024-006',
        inspectionDate: '2024-04-04',
        priority: 'High'
      },
      {
        id: 5,
        title: 'Emergency Preparedness Audit Report',
        property: 'Downtown Office Complex',
        client: 'Property Management Inc',
        reportType: 'Audit Report',
        category: 'Emergency Preparedness',
        date: '2024-04-05',
        status: 'Completed',
        format: 'PDF',
        pages: 25,
        findings: 'Emergency procedures current. First aid kit restocking required.',
        compliance: 'Compliant',
        photos: 20,
        attachments: 8,
        reportNumber: 'EP-2024-002',
        inspectionDate: '2024-04-01',
        priority: 'Medium'
      },
      {
        id: 6,
        title: 'Weekly Progress Report - Multiple Properties',
        property: 'Various',
        client: 'Multiple Clients',
        reportType: 'Progress Report',
        category: 'General',
        date: '2024-04-14',
        status: 'Pending',
        format: 'Excel',
        pages: 5,
        findings: 'Week summary: 8 inspections completed, 3 pending, 2 overdue.',
        priority: 'Low'
      }
    ]);
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  // Quick Action Handlers
  const handleCreateReport = () => {
    const completed = reports.filter((r) => r.status.toLowerCase().includes('completed'));
    const base = completed[0] || reports[0];
    if (!base) {
      toast.warning('No inspection data available to generate a report');
      return;
    }
    generateContractorReport({
      title: `Generated Report - ${base.property}`,
      type: 'inspection',
      propertyId: base.property_id || 1,
      data: {
        findings: base.findings,
        compliance: base.compliance,
        category: base.category,
        priority: base.priority
      }
    })
      .then(() => {
        toast.success('Report generated');
        fetchReportsData();
      })
      .catch((error) => toast.error(error.message));
  };

  const handleUploadDocument = () => {
    // Create a hidden file input to trigger file upload
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.jpg,.png';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        const firstReport = reports[0];
        if (!firstReport) {
          toast.warning('Create or select a property report first');
          return;
        }
        uploadContractorReportFiles({
          propertyId: firstReport.property_id || 1,
          title: `Uploaded files - ${firstReport.property}`,
          type: 'inspection',
          files
        })
          .then(() => {
            toast.success(`Uploaded ${files.length} file(s)`);
            fetchReportsData();
          })
          .catch((error) => toast.error(error.message));
      }
    };
    input.click();
  };

  const handleArchiveReports = () => {
    const completedReports = reports.filter(r => r.status === 'Completed');
    if (completedReports.length === 0) {
      toast.warning('No completed reports available to archive');
    } else {
      Promise.all(completedReports.map((report) => archiveContractorReport(report.id)))
        .then(() => {
          toast.success(`${completedReports.length} reports archived`);
          fetchReportsData();
        })
        .catch((error) => toast.error(error.message));
    }
  };

  const handleExportAll = () => {
    reports.forEach((report) => {
      window.open(getContractorReportDownloadUrl(report.id), '_blank');
    });
    toast.success('Report export started');
  };

  const handleDownloadReport = (reportId) => {
    const report = reports.find(r => r.id === reportId);
    if (report) {
      window.open(getContractorReportDownloadUrl(reportId), '_blank');
      toast.success(`Downloading ${report.title}`);
    }
  };

  const handlePrintReport = (reportId) => {
    const report = reports.find(r => r.id === reportId);
    if (report) {
      window.open(getContractorReportDownloadUrl(reportId), '_blank');
      window.print();
      toast.info(`Preparing ${report.title} for printing...`);
    }
  };

  const handleShareReport = (reportId) => {
    const report = reports.find(r => r.id === reportId);
    if (report) {
      // The server can't open these links yet (the token is never stored), so
      // don't hand out a link that 404s. Download and send the PDF instead.
      toast.info(`Share links aren't available for ${report.title} yet. Download the report and send the PDF instead.`);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return '#27ae60';
      case 'sent': return '#3498db';
      case 'pending review': return '#f39c12';
      case 'pending': return '#f39c12';
      case 'draft': return '#95a5a6';
      default: return '#95a5a6';
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return <FaCheckCircle />;
      case 'sent': return <FaShare />;
      case 'pending review': return <FaClock />;
      case 'pending': return <FaClock />;
      case 'draft': return <FaEdit />;
      default: return <FaFileAlt />;
    }
  };

  const getComplianceColor = (compliance) => {
    switch (compliance?.toLowerCase()) {
      case 'compliant': return '#27ae60';
      case 'non-compliant': return '#e74c3c';
      case 'partially compliant': return '#f39c12';
      default: return '#95a5a6';
    }
  };

  const getReportIcon = (format) => {
    switch (format?.toLowerCase()) {
      case 'pdf': return <FaFilePdf style={{ color: '#e74c3c' }} />;
      case 'word': return <FaFileWord style={{ color: '#3498db' }} />;
      case 'excel': return <FaFileExcel style={{ color: '#27ae60' }} />;
      default: return <FaFileAlt />;
    }
  };

  const filteredReports = reports.filter(report => {
    if (filter === 'all') return true;
    return report.status.toLowerCase().replace(' ', '_') === filter.replace('_', ' ');
  });

  const sortedReports = [...filteredReports].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.date) - new Date(a.date);
      case 'type':
        return a.reportType.localeCompare(b.reportType);
      case 'status':
        return a.status.localeCompare(b.status);
      case 'property':
        return a.property.localeCompare(b.property);
      default:
        return 0;
    }
  });

  return (
    <div className="contractor-dashboard">
      <header className="contractor-header">
        <button className="mobile-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle menu">
          {sidebarOpen ? <FaTimes /> : <FaBars />}
        </button>
        <img src={allSafeLogo} alt="CloudSafe" className="header-logo" />
        <div className="header-right">
          <nav className="nav-links">
            <Link to="/contractor/dashboard" className="nav-link">Dashboard</Link>
            <Link to="/contractor/tasks" className="nav-link">My Tasks</Link>
            <Link to="/contractor/inspections" className="nav-link">Inspections</Link>
            <Link to="/contractor/reports" className="nav-link active">Reports</Link>
          </nav>
          <div className="header-icons">
            <div className="icon"><FaSearch /></div>
            <div className="icon"><FaBell /></div>
            <div className="user-avatar-container" aria-haspopup="menu" aria-expanded={showUserMenu} {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="user-menu">
                  <Link to="/contractor/profile" onClick={() => setShowUserMenu(false)}>Profile</Link>
                  <button onClick={handleLogout}>Logout</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="contractor-layout">
        <div className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)} />
        <aside className={`contractor-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-section">
            <h3>Navigation</h3>
            <nav className="sidebar-nav">
              <Link to="/contractor/dashboard" className="nav-item" onClick={() => setSidebarOpen(false)}>
                <FaChartLine /><span>Dashboard</span>
              </Link>
              <Link to="/contractor/tasks" className="nav-item" onClick={() => setSidebarOpen(false)}>
                <FaTasks /><span>My Tasks</span>
              </Link>
              <Link to="/contractor/inspections" className="nav-item" onClick={() => setSidebarOpen(false)}>
                <FaClipboardList /><span>Inspections</span>
              </Link>
              <Link to="/contractor/reports" className="nav-item active" onClick={() => setSidebarOpen(false)}>
                <FaFileAlt /><span>Reports</span>
              </Link>
            </nav>
          </div>
          <div className="sidebar-section">
            <h3>Quick Actions</h3>
            <nav className="sidebar-nav">
              <button className="nav-item" onClick={() => { handleCreateReport(); setSidebarOpen(false); }}>
                <FaFileSignature /><span>Create Report</span>
              </button>
              <button className="nav-item" onClick={() => { handleUploadDocument(); setSidebarOpen(false); }}>
                <FaUpload /><span>Upload Document</span>
              </button>
              <button className="nav-item" onClick={() => { handleArchiveReports(); setSidebarOpen(false); }}>
                <FaArchive /><span>Archive Reports</span>
              </button>
            </nav>
          </div>
        </aside>

        <main className="contractor-content">
          <div className="con-page-header">
            <div>
              <h1>Reports</h1>
              <p className="con-page-subtitle">Generate, manage, and share inspection reports</p>
            </div>
            <div className="header-actions">
              <button className="btn-secondary" onClick={handleCreateReport}><FaFileSignature /> Create Report</button>
              <button className="btn-primary" onClick={handleExportAll}><FaDownload /> Export All</button>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card reports">
              <div className="stat-icon"><FaFileAlt /></div>
              <div className="stat-content">
                <h3>Total Reports</h3>
                <p className="stat-number">{reports.length}</p>
              </div>
            </div>
            <div className="stat-card completed">
              <div className="stat-icon"><FaCheckCircle /></div>
              <div className="stat-content">
                <h3>Completed</h3>
                <p className="stat-number">{reports.filter(r => r.status === 'Completed').length}</p>
              </div>
            </div>
            <div className="stat-card pending">
              <div className="stat-icon"><FaClock /></div>
              <div className="stat-content">
                <h3>Pending</h3>
                <p className="stat-number">{reports.filter(r => r.status.includes('Pending')).length}</p>
              </div>
            </div>
            <div className="stat-card properties">
              <div className="stat-icon"><FaEdit /></div>
              <div className="stat-content">
                <h3>Drafts</h3>
                <p className="stat-number">{reports.filter(r => r.status === 'Draft').length}</p>
              </div>
            </div>
          </div>

          <div className="task-controls">
            <div className="filter-controls">
              <label>Status:</label>
              <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">All Reports</option>
                <option value="completed">Completed</option>
                <option value="pending_review">Pending Review</option>
                <option value="pending">Pending</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
              </select>
            </div>
            <div className="sort-controls">
              <label>Sort:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="date">Date</option>
                <option value="type">Report Type</option>
                <option value="status">Status</option>
                <option value="property">Property</option>
              </select>
            </div>
            <div className="sort-controls">
              <label>Period:</label>
              <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
              </select>
            </div>
          </div>

          <div className="tasks-list">
            {sortedReports.map(report => (
              <div key={report.id} className="task-card">
                <div className="task-header">
                  <div className="task-info">
                    <h3>{report.title}</h3>
                    <div className="task-meta">
                      <span className="property-name"><FaBuilding /> {report.property}</span>
                      <span className="client-name"><FaUsers /> {report.client}</span>
                      <span className="report-number"><FaFileAlt /> {report.reportNumber}</span>
                    </div>
                  </div>
                  <div className="task-status-info">
                    <span className="report-format">{getReportIcon(report.format)} {report.format}</span>
                    <span className={`status ${report.status.toLowerCase().replace(' ', '-')}`}>
                      {getStatusIcon(report.status)} {report.status}
                    </span>
                  </div>
                </div>

                <div className="task-details">
                  <div className="detail-grid">
                    <div className="detail-item"><FaCalendarAlt className="icon" /><span>Created: {report.date}</span></div>
                    <div className="detail-item"><FaCertificate className="icon" /><span>{report.reportType} &middot; {report.category}</span></div>
                    <div className="detail-item"><FaFileAlt className="icon" /><span>{report.pages} pages &middot; {report.photos} photos</span></div>
                    {report.compliance && (
                      <div className="detail-item">
                        <FaShieldAlt className="icon" />
                        <span style={{ color: getComplianceColor(report.compliance), fontWeight: '600' }}>{report.compliance}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="report-findings">
                  <h4>Key Findings:</h4>
                  <p>{report.findings}</p>
                </div>

                {report.actions && (
                  <div className="report-actions-required">
                    <h4>Actions Required:</h4>
                    <p>{report.actions}</p>
                    {report.deadline && (
                      <p className="deadline"><FaClock /> Deadline: {report.deadline}</p>
                    )}
                  </div>
                )}

                {report.nextInspection && (
                  <div className="next-inspection"><FaCalendar /> Next Inspection: {report.nextInspection}</div>
                )}

                <div className="task-actions">
                  <button className="action-btn" onClick={() => handleDownloadReport(report.id)}><FaDownload /> Download</button>
                  <button className="action-btn" onClick={() => handlePrintReport(report.id)}><FaPrint /> Print</button>
                  {report.status === 'Completed' && (
                    <button className="action-btn" onClick={() => handleShareReport(report.id)}><FaShare /> Share</button>
                  )}
                  <button className="action-btn"><FaEye /> Details</button>
                  {report.status === 'Draft' && (
                    <button className="action-btn"><FaEdit /> Edit</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ContractorReports; 